import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  StackProps,
} from '@mui/material'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { translateCustomComponent } from '@/graphql/mutations'
import { useOrg, useUrlParams } from '@/hooks'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { AmplifyGraphQLclient } from '@/utils/amplify'
import countryCodeOptions from '@/utils/countryCodeOptions'

import { useConfirm } from '../ConfirmDialogContext'
import { JsonDataViewer } from '../JsonViewer'

export type LanguageSelectorProps = {
  sx?: StackProps['sx']
}
export default function LanguageSelector(props: LanguageSelectorProps) {
  const { sx = { alignSelf: 'flex-end', gap: 2 } } = props
  const { orgID, componentID } = useParams()
  const [{ lang = 'en' }, setParams] = useUrlParams()
  const changeLang = (newLang: string) => () => {
    setParams({ lang: newLang !== 'en' ? newLang : null })
  }
  const org = useOrg(orgID)
  const component = useGetCustomComponent(componentID)
  const confirm = useConfirm()
  const translate = async () => {
    const langs = org.data?.languagesSupported || []
    const languages = langs.map((l) =>
      countryCodeOptions.find(({ value }) => value === l)
    )
    try {
      await confirm({
        cancellationText: 'Cancel',
        confirmationText: 'Translate',
        content: `This will translate from ${
          languages.find((l) => l?.value === lang)?.label
        } to ${langs
          ?.filter((l) => l !== lang)
          .map(
            (l) => countryCodeOptions.find(({ value }) => value === l)?.label
          )
          .join(', ')}`,
        title: 'Translate',
      })
      setOpen(true)

      const { data } = await AmplifyGraphQLclient.graphql({
        query: translateCustomComponent,
        variables: {
          data: JSON.stringify(component.data?.settings),
          from: lang,
          to: langs.filter((l) => l !== lang),
        },
      })
      setNewData(JSON.parse(data.translateCustomComponent))
    } catch (error) {}
  }

  const [open, setOpen] = useState(false)
  const [newData, setNewData] = useState(null)
  const updateCustomComponent = useUpdateCustomComponent(componentID)
  const updateComponent = async () => {
    await updateCustomComponent.mutateAsync({
      settings: newData,
    })
    setOpen(false)
    setNewData(null)
  }
  if (!org.data?.languagesSupported) return null

  return (
    <Stack direction="row" spacing={2} sx={sx}>
      <ButtonGroup variant="contained">
        {org.data.languagesSupported.map((supportedLang) => (
          <Button
            key={supportedLang}
            variant={lang === supportedLang ? 'contained' : 'outlined'}
            onClick={changeLang(supportedLang)}
          >
            {supportedLang}
          </Button>
        ))}
      </ButtonGroup>

      <Button variant="outlined" onClick={translate}>
        Translate
      </Button>
      <Dialog open={open}>
        <DialogTitle>
          {!newData ? 'Translating component...' : 'Component translated'}
        </DialogTitle>
        <DialogContent>
          {!!newData ? (
            <JsonDataViewer
              data={newData}
              rootName={component.data?.name || ''}
            />
          ) : (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                p: 2,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {!!newData && <Button onClick={updateComponent}>Continue</Button>}
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
